import axios from 'axios'
import { AUTH_URL, API_URL, getAuthHeader } from '@/services/api.service.js'
import { logger } from '@/utils/logger'

export const checkAccountStatus = async (email) => {
  try {
    const res = await axios.post(`${AUTH_URL}/users/auth-type`, {
      data: {
        email
      }
    })
    return res
  } catch (error) {
    logger.info(error)
    return null
  }
}

// Ensures MFA claims are properly set by triggering backend middleware
export const validateAuthState = async () => {
  try {
    const res = await axios.get(`${await API_URL()}/users/auth-state`, {
      headers: { ...(await getAuthHeader()) }
    })
    return res.data
  } catch (error) {
    logger.info('Error ensuring auth state:', error)
    return null
  }
}

// does not require email or phone and check against logged in user
export const sendLoginOTP = async () => {
  try {
    const res = await axios.post(
      `${await API_URL()}/users/send-login-otp`,
      {
        data: {}
      },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

// requires phone and check against logged in user
export const sendOTP = async (phone) => {
  try {
    const res = await axios.post(
      `${await API_URL()}/users/send-otp`,
      {
        data: {
          phone
        }
      },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

// requires phone and check against logged in user
export const verifyOTP = async (phone, verificationCode) => {
  try {
    const res = await axios.post(
      `${await API_URL()}/users/verify-otp`,
      {
        data: {
          phone,
          verificationCode
        }
      },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res.data
  } catch (error) {
    return error.response.status == 500
      ? { status: false, message: 'Something went wrong, try log in again' }
      : error.response.data
  }
}

// check against logged in user by email
export const verifyLoginOTP = async (verificationCode) => {
  try {
    const res = await axios.post(
      `${await API_URL()}/users/verify-login-otp`,
      {
        data: {
          verificationCode
        }
      },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res.data
  } catch (error) {
    return error.response.status == 500
      ? { status: false, message: 'Something went wrong, try log in again' }
      : error.response.data
  }
}

/**
 * Sends a failed login attempt to the server.
 * @param {string} email - The email address used for the login attempt.
 * @returns {Promise<Object|null>} - A Promise that resolves to the response object if the request is successful, or null if there is an error.
 */
export const failedLoginAttempt = async (email) => {
  try {
    const res = await axios.post(`${AUTH_URL}/users/failed-login-attempt`, {
      data: {
        email
      }
    })
    return res
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const setEmailVerified = async () => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/users/verify-email`,
      { data: {} },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const changedPassword = async ({ email, password }) => {
  try {
    const res = await axios.post(`${AUTH_URL}/users/changed-password`, {
      data: {
        email,
        password
      }
    })
    return res.data
  } catch (error) {
    logger.error('changedPassword API error:', error.message)
    return {
      status: 'error',
      message: error.response?.data?.message || 'Failed to change password. Please try again.'
    }
  }
}

/**
 * Resets the password for a user.
 *
 * @param {string} email - The email of the user.
 * @returns {Promise<Object|null>} - A promise that resolves to the response object if successful, or null if there was an error.
 */
export const resetPassword = async (email) => {
  try {
    const res = await axios.post(`${AUTH_URL}/users/reset-password`, {
      data: {
        email
      }
    })
    return res
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const logout = async () => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/users/logout`,
      { data: {} },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const restoreMfaVerifiedClaim = async () => {
  try {
    const res = await axios.post(
      `${await API_URL()}/users/restore-mfa-verified-claim`,
      {
        data: {}
      },
      { headers: { ...(await getAuthHeader()) } }
    )
    return res.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const getMFACooldownStatus = async () => {
  try {
    const res = await axios.get(`${await API_URL()}/users/mfa-cooldown-status`, {
      headers: { ...(await getAuthHeader()) }
    })
    return res.data
  } catch (error) {
    logger.info(error)
    return null
  }
}
